import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Stack,
  OutlinedInput,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { STATUS_CONFIG, CALENDAR_EVENT_TYPES } from "../constants";
import { validateClosingHour } from "../../../utils/formatters";
import TimeInput24H from "../../../components/TimeInput24H";
import { SERVICE_TYPES } from "../../Servicios/constants";
import { isInternalCalendarEvent } from "../utils";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    fontSize: 13,
  },
  "& .MuiInputLabel-root": { fontSize: 13 },
};

export const ServiceEditForm = ({
  formData,
  technicians = [],
  onFieldChange,
  onSave,
  onCancel,
  loading,
  service,
}) => {
  const closingHour = service?.branch?.closingHour || null;
  const selectedTechIds = formData.technicians || [];
  const isEvent = isInternalCalendarEvent(service);

  const timeValidation = useMemo(
    () =>
      isEvent
        ? { isValid: true, message: "" }
        : validateClosingHour(formData.date, formData.hour, closingHour),
    [formData.date, formData.hour, closingHour, isEvent]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!timeValidation.isValid) return;
    onSave();
  };

  const showTimeError = !isEvent && !!formData.hour && !timeValidation.isValid;
  const clientName = service?.client?.name || service?.client || "";
  const branchName = service?.branch?.name || service?.branch || "";
  const place = [clientName, branchName].filter(Boolean).join(" · ");

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <Box sx={{ px: 1.6, pt: 1.35, pb: 1.15, borderBottom: "1px solid #EEE" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111", letterSpacing: "-0.02em" }}>
          {isEvent ? "Editar evento" : "Editar servicio"}
        </Typography>
        {isEvent ? (
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#667085" }}>
            {formData.title || "Evento interno"}
          </Typography>
        ) : place ? (
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#667085" }}>{place}</Typography>
        ) : null}
      </Box>

      <Box sx={{ px: 1.6, py: 1.35, display: "flex", flexDirection: "column", gap: 1.15 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField
            label="Fecha"
            type="date"
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={(e) => onFieldChange("date", e.target.value)}
            disabled={loading}
            sx={fieldSx}
          />
          <TimeInput24H
            label="Hora"
            size="small"
            required
            value={formData.hour}
            onChange={(e) => onFieldChange("hour", e.target.value)}
            disabled={loading}
            error={showTimeError}
            helperText={showTimeError ? timeValidation.message : undefined}
            FormHelperTextProps={{ sx: { fontSize: "0.7rem", mx: 0 } }}
            sx={fieldSx}
          />
        </Box>

        {isEvent ? (
          <TextField
            label="Título"
            size="small"
            required
            value={formData.title || ""}
            onChange={(e) => onFieldChange("title", e.target.value)}
            disabled={loading}
            sx={fieldSx}
          />
        ) : null}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          {isEvent ? (
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={formData.eventType || ""}
                onChange={(e) => onFieldChange("eventType", e.target.value)}
                disabled={loading}
              >
                {CALENDAR_EVENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={formData.type || ""}
                onChange={(e) => onFieldChange("type", e.target.value)}
                disabled={loading}
              >
                {SERVICE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>
                    {t.label}
                  </MenuItem>
                ))}
                {formData.type && !SERVICE_TYPES.some((t) => t.value === formData.type) ? (
                  <MenuItem value={formData.type} sx={{ fontSize: 13 }}>
                    {formData.type}
                  </MenuItem>
                ) : null}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={formData.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              disabled={loading}
            >
              {Object.entries(STATUS_CONFIG)
                .filter(([key]) => key !== "COMPLETED")
                .map(([key, { label }]) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Técnicos</InputLabel>
          <Select
            multiple
            label="Técnicos"
            value={selectedTechIds}
            onChange={(e) => onFieldChange("technicians", e.target.value)}
            disabled={loading}
            input={<OutlinedInput label="Técnicos" />}
            renderValue={(ids) => {
              const names = technicians
                .filter((t) => ids.includes(t._id))
                .map((t) => t.name);
              return names.length ? names.join(", ") : "Sin asignar";
            }}
          >
            {technicians.map((tech) => (
              <MenuItem key={tech._id} value={tech._id} sx={{ fontSize: 13 }}>
                {tech.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isEvent ? (
          <TextField
            label="Ubicación"
            size="small"
            value={formData.location || ""}
            onChange={(e) => onFieldChange("location", e.target.value)}
            disabled={loading}
            placeholder="Oficina, taller..."
            sx={fieldSx}
          />
        ) : null}

        <TextField
          label="Notas"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={formData.notes || ""}
          onChange={(e) => onFieldChange("notes", e.target.value)}
          disabled={loading}
          placeholder="Notas adicionales"
          size="small"
          sx={fieldSx}
        />

        {!isEvent && closingHour ? (
          <Typography sx={{ fontSize: 11.5, color: "#8A8F98" }}>
            Cierre de sucursal: {closingHour}
            {showTimeError ? ` · ${timeValidation.message}` : ""}
          </Typography>
        ) : null}
      </Box>

      <Stack direction="row" spacing={0.7} sx={{ px: 1.6, pb: 1.3 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: "#E5E7EB",
            color: "#111",
            py: 0.65,
            fontSize: 12.5,
            "&:hover": { borderColor: "#111", bgcolor: "transparent" },
          }}
        >
          Cancelar
        </Button>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disableElevation
          disabled={loading || !timeValidation.isValid}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            py: 0.65,
            fontSize: 12.5,
            bgcolor: "#111",
            "&:hover": { bgcolor: "#222" },
          }}
        >
          {loading ? "Guardando" : "Guardar"}
        </Button>
      </Stack>
    </Box>
  );
};
